import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode'
import { reportScan } from './scanTelemetry'

// html5-qrcode guards `stop`, `pause` and `resume` with a *synchronous* `throw`
// of a bare string when the scanner is in the wrong state:
//
//     if (!this.stateManagerProxy.isScanning()) {
//       throw "Cannot stop, scanner is not running or paused.";
//     }
//
// A `.catch()` on the returned promise never sees it, so the throw escapes the
// React effect cleanup and the app's Sentry ErrorBoundary replaces the whole
// gym portal with "Something went wrong". The reported case is the easy one to
// hit: `start()` rejects (no camera on the front-desk PC, or permission
// denied), the effect sets `cameraEnabled` back to false, the cleanup calls
// `stop()` on a scanner that never started, and the page dies.
//
// Every call goes through these wrappers, which check the state first and still
// report anything unexpected instead of throwing.

const STATE_NAMES: Record<number, string> = {
  [Html5QrcodeScannerState.UNKNOWN]: 'unknown',
  [Html5QrcodeScannerState.NOT_STARTED]: 'not_started',
  [Html5QrcodeScannerState.SCANNING]: 'scanning',
  [Html5QrcodeScannerState.PAUSED]: 'paused',
}

export function scannerState(scanner: Html5Qrcode | null): number | null {
  if (!scanner?.getState) return null
  try {
    return scanner.getState()
  } catch {
    return null
  }
}

export function scannerStateName(scanner: Html5Qrcode | null): string {
  const state = scannerState(scanner)
  return state === null ? 'unavailable' : (STATE_NAMES[state] ?? `state_${state}`)
}

/** Stops the camera if it is running or paused. Never throws. */
export async function safeStop(scanner: Html5Qrcode | null): Promise<void> {
  if (!scanner) return
  const state = scannerState(scanner)
  const from = scannerStateName(scanner)

  if (state === Html5QrcodeScannerState.NOT_STARTED) {
    // The common, expected case — `start()` failed or never resolved.
    reportScan('camera stop skipped, the scanner was never running', {
      stage: 'camera',
      level: 'info',
      data: { state: from },
    })
    return
  }

  try {
    await scanner.stop()
  } catch (error) {
    reportScan('camera stop failed', {
      stage: 'camera',
      level: 'warning',
      error,
      data: { state: from },
    })
  }

  try {
    await scanner.clear()
  } catch (error) {
    reportScan('camera clear failed', {
      stage: 'camera',
      level: 'warning',
      error,
      data: { state: from },
    })
  }
}

/** Pauses the camera if it is scanning. Never throws. Returns whether it paused. */
export function safePause(scanner: Html5Qrcode | null): boolean {
  if (!scanner) return false
  const state = scannerState(scanner)
  if (state !== null && state !== Html5QrcodeScannerState.SCANNING) return false

  try {
    scanner.pause(true)
    return true
  } catch (error) {
    reportScan('camera pause failed', {
      stage: 'camera',
      level: 'warning',
      error,
      data: { state: scannerStateName(scanner) },
    })
    return false
  }
}

/** Resumes a paused camera. Never throws. Returns whether it resumed. */
export function safeResume(scanner: Html5Qrcode | null): boolean {
  if (!scanner) return false
  const state = scannerState(scanner)
  if (state !== null && state !== Html5QrcodeScannerState.PAUSED) return false

  try {
    scanner.resume()
    return true
  } catch (error) {
    reportScan('camera resume failed', {
      stage: 'camera',
      level: 'warning',
      error,
      data: { state: scannerStateName(scanner) },
    })
    return false
  }
}
